// Live updates over Socket.IO (namespace /app). The handshake carries the in-memory access token; the
// server drops the socket when the token expires and the client reconnects with the fresh one.
// Rooms are chosen by the server; clients may only ask to follow a topic (e.g. a project), which the
// server authorises.
import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { currentAccessToken, refreshSession } from './api.js';

let socket: Socket | null = null;

export function connectRealtime(): Socket {
  if (socket) return socket;
  socket = io('/app', {
    path: '/socket.io',
    transports: ['websocket'],
    auth: (cb) => cb({ token: currentAccessToken() }),
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 15_000,
  });
  socket.on('connect_error', () => {
    // Usually an expired token: refresh it; the next automatic retry uses the new one.
    void refreshSession();
  });
  return socket;
}

export function disconnectRealtime(): void {
  socket?.disconnect();
  socket = null;
}

/** Subscribes to a server event while the component is mounted. */
export function useRealtime<T = unknown>(event: string, handler: (data: T) => void): void {
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    const s = connectRealtime();
    const fn = (data: T) => ref.current(data);
    s.on(event, fn);
    return () => {
      s.off(event, fn);
    };
  }, [event]);
}

/** Follows a topic room (e.g. `project`, id) while mounted; the server checks access. */
export function useTopic(topic: string, id: string | undefined): void {
  useEffect(() => {
    if (!id) return;
    const s = connectRealtime();
    const join = () => s.emit('subscribe', { topic, id });
    join();
    s.on('connect', join);
    return () => {
      s.off('connect', join);
      s.emit('unsubscribe', { topic, id });
    };
  }, [topic, id]);
}
